import React, { useState, useEffect, useMemo } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAutocomplete from "components/MDAutocomplete";
import MDTextField from "components/MDTextField";
import {
  Grid,
  Card,
  Box,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  Button,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { MaterialReactTable } from "material-react-table";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { IconTableExport } from "@tabler/icons-react";
import handleExport from "utils/exportData";

const STATUS_OPTIONS = [
  { title: "Active", value: 2 },
  { title: "Inactive", value: 3 },
];

const DEFAULT_STATUS = STATUS_OPTIONS[0]; // Active by default

function PhaseManage() {
  const [phaseName, setPhaseName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(DEFAULT_STATUS);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);

  // Table state
  const [data, setData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const fetchPhases = async (
    pageIndex = pagination.pageIndex,
    pageSize = pagination.pageSize,
    sort = sorting
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex + 1,
        limit: pageSize,
      });
      if (sort.length > 0) {
        queryParams.append("sortField", sort[0].id);
        queryParams.append("sortOrder", sort[0].desc ? "desc" : "asc");
      }
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/green-school-phases/all?${queryParams.toString()}`,
        { withCredentials: true }
      );
      if (res.data.status === 200) {
        setData(res.data.data || []);
        setRowCount(res.data.total || 0);
      }
    } catch (err) {
      console.error("Error fetching phases:", err);
    }
    setLoading(false);
  };

  useQuery({
    queryKey: ["phases", pagination, sorting],
    queryFn: () => fetchPhases(pagination.pageIndex, pagination.pageSize, sorting),
    keepPreviousData: true,
  });

  const validateForm = () => {
    const newErrors = {};
    if (!phaseName.trim()) newErrors.phaseName = "Phase name is required.";
    else if (phaseName.trim().length > 100) newErrors.phaseName = "Phase name must be at most 100 characters.";
    if (!selectedStatus) newErrors.selectedStatus = "Status is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = { name: phaseName.trim(), status: selectedStatus.value };
      let res;
      if (isEditMode) {
        res = await axios.put(
          `${process.env.REACT_APP_API_URL}/green-school-phases/${editingId}`,
          payload,
          { withCredentials: true }
        );
      } else {
        res = await axios.post(
          `${process.env.REACT_APP_API_URL}/green-school-phases/add`,
          payload,
          { withCredentials: true }
        );
      }
      setSnackbar({ open: true, message: res.data.message, severity: "success" });
      clearForm();
      fetchPhases();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Something went wrong.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const clearForm = () => {
    setPhaseName("");
    setSelectedStatus(DEFAULT_STATUS); // Reset to Active by default
    setErrors({});
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleEdit = (row) => {
    setPhaseName(row.name);
    setSelectedStatus(STATUS_OPTIONS.find((s) => s.value === row.status) || DEFAULT_STATUS);
    setIsEditMode(true);
    setEditingId(row.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/green-school-phases/all?page=1&limit=10000`,
        { withCredentials: true }
      );
      if (res.data.status === 200) {
        const exportRows = res.data.data.map((row) => ({
          "Phase Name": row.name,
          "Status": row.status_name,
          "Created By": row.created_by_name || "-",
          "Updated By": row.updated_by_name || "-",
          "Created At": row.created_at ? new Date(row.created_at).toLocaleDateString("en-IN") : "-",
          "Updated At": row.updated_at ? new Date(row.updated_at).toLocaleDateString("en-IN") : "-",
        }));
        await handleExport(exportRows, "Green_School_Phases_Export");
        setSnackbar({ open: true, message: "Excel downloaded successfully!", severity: "success" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Export failed.", severity: "error" });
    }
    setLoading(false);
  };

  const columns = useMemo(
    () => [
      { accessorKey: "name", header: "Phase Name", size: 300 },
      {
        accessorKey: "status_name",
        header: "Status",
        size: 150,
        Cell: ({ row }) => (
          <Chip
            label={row.original.status_name}
            color={row.original.status === 2 ? "success" : "error"}
            size="small"
            sx={{ fontWeight: 600, "& .MuiChip-label": { color: "white !important" } }}
          />
        ),
      },
      { accessorKey: "created_by_name", header: "Created By", size: 200 },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip title="Edit Phase">
            <IconButton
              size="small"
              onClick={() => handleEdit(row.original)}
              sx={{
                color: "#f48c06",
                backgroundColor: "#fff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: "#f48c06", color: "#fff" },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <MDBox mt={4} mb={3}>
        <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h4" fontWeight="bold">
            Manage Phases
          </MDTypography>
        </MDBox>

        {/* Add / Edit Form */}
        <Card>
          <MDBox p={{ xs: 2, sm: 3, md: 4 }}>
            <MDTypography variant="h5" fontWeight="medium" mb={2}>
              {isEditMode ? "Edit Phase" : "Add New Phase"}
            </MDTypography>
            {/* Phase Name and Status on same line */}
            <Stack spacing={4}>
              <Grid container spacing={3}>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <MDTextField
                    name="phaseName"
                    placeholder="Enter Phase Name"
                    displayName="Phase Name"
                    value={phaseName}
                    onChange={(val) => {
                      setPhaseName(val);
                      if (errors.phaseName) setErrors({ ...errors, phaseName: "" });
                    }}
                    required={true}
                    error={!!errors.phaseName}
                    helperText={errors.phaseName}
                    maxLength={100}
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <MDAutocomplete
                    label="Status"
                    name="status"
                    options={STATUS_OPTIONS}
                    value={selectedStatus?.value ?? 2}
                    onChange={(val) => {
                      const found = STATUS_OPTIONS.find((s) => s.value === val);
                      setSelectedStatus(found || DEFAULT_STATUS);
                      if (errors.selectedStatus) setErrors({ ...errors, selectedStatus: "" });
                    }}
                    required={true}
                    error={!!errors.selectedStatus}
                    helperText={errors.selectedStatus}
                  />
                </Grid>
                {/* Save and Cancel always visible */}
                <Grid item xs={12} md={4} display="flex" gap={1} alignItems="flex-end">
                  <MDButton
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                      color: "white !important",
                      backgroundColor: "#16610E",
                      "&:hover": { backgroundColor: "#1f4d1a" },
                      flex: 1,
                      height: "40px",
                    }}
                  >
                    {loading ? <CircularProgress size={18} color="inherit" /> : isEditMode ? "Update" : "Save"}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    onClick={clearForm}
                    disabled={loading}
                    sx={{
                      flex: 1,
                      height: "40px",
                      color: "#b71c1c !important",
                      borderColor: "#b71c1c",
                      "&:hover": { backgroundColor: "#fbe9e7", borderColor: "#b71c1c" },
                    }}
                  >
                    Cancel
                  </MDButton>
                </Grid>
              </Grid>
            </Stack>
          </MDBox>
        </Card>

        {/* Table */}
        <MDBox mt={4}>
          <Card>
            <MDBox pt={3} px={3} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5" fontWeight="bold">
                Phase List
              </MDTypography>
            </MDBox>
            <MDBox p={3}>
              <MaterialReactTable
                columns={columns}
                data={data}
                layoutMode="grid"
                manualPagination
                manualSorting
                enableGlobalFilter={false}
                enableFullScreenToggle={false}
                enableColumnFilters={false}
                enableColumnActions={false}
                enableDensityToggle={false}
                rowCount={rowCount}
                state={{ isLoading: loading, pagination, sorting }}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                renderTopToolbarCustomActions={() => (
                  <Box sx={{ display: "flex", gap: 1, p: "4px", marginLeft: "auto", }}>
                    <Tooltip title="Export to Excel" arrow>
                      <Button
                        onClick={handleExportData}
                        startIcon={<IconTableExport size={18} />}
                        sx={{
                          background: "linear-gradient(90deg, rgba(2,183,255,0.65) 30%, rgb(110,199,235) 100%)",
                          color: "#fff",
                          borderRadius: "8px",
                          px: 2,
                          py: 1,
                          "&:hover": {
                            background: "linear-gradient(90deg, rgba(55,198,254,0.97) 30%, rgb(39,169,220) 100%)",
                            transform: "scale(1.03)",
                          },
                        }}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                  </Box>
                )}
                muiTableHeadCellProps={{
                  sx: {
                    backgroundColor: "#004030",
                    color: "#fff",
                    fontWeight: "bold",
                    "& .MuiTableSortLabel-icon": { color: "#fff !important" },
                    "& .MuiIconButton-root": { color: "#fff" },
                  },
                }}
                muiTableBodyRowProps={({ row }) => ({
                  sx: { backgroundColor: row.index % 2 === 0 ? "#fff" : "#0846361c" },
                })}
                muiTopToolbarProps={{ sx: { backgroundColor: "#fff", boxShadow: "none" } }}
              />
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "white" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default PhaseManage;
